import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, unwrap } from '../../lib/api/client'
import {
  dismissCliqApiV1CliqSuggestionIdDismissPost,
  fileCliqApiV1CliqSuggestionIdFilePost,
  listCaseCliqApiV1CasesCaseIdCliqGet,
  listUnroutedCliqApiV1CliqUnroutedGet,
  releaseCliqApiV1CliqSuggestionIdReleasePost,
} from '../../lib/api/generated'

/** A Cliq message or /case note waiting for a person to file it (#1023). */

export const cliqKeys = {
  all: ['cliq'] as const,
  case: (caseId: string) => ['cliq', 'case', caseId] as const,
  unrouted: ['cliq', 'unrouted'] as const,
}

export const caseCliqQuery = (caseId: string) =>
  queryOptions({
    queryKey: cliqKeys.case(caseId),
    queryFn: () => unwrap(listCaseCliqApiV1CasesCaseIdCliqGet({ path: { case_id: caseId } })),
    staleTime: 15_000,
    retry: (count, error) => {
      if (error instanceof ApiError && (error.notBuilt || error.status === 404)) return false
      return count < 1
    },
  })

export const unroutedCliqQuery = () =>
  queryOptions({
    queryKey: cliqKeys.unrouted,
    queryFn: () => unwrap(listUnroutedCliqApiV1CliqUnroutedGet()),
    staleTime: 15_000,
    retry: (count, error) => {
      if (error instanceof ApiError && (error.notBuilt || error.status === 404)) return false
      return count < 1
    },
  })

function refresh(queryClient: ReturnType<typeof useQueryClient>, caseId?: string) {
  void queryClient.invalidateQueries({ queryKey: cliqKeys.all })
  if (caseId) void queryClient.invalidateQueries({ queryKey: cliqKeys.case(caseId) })
  void queryClient.invalidateQueries({ queryKey: ['case'] })
  void queryClient.invalidateQueries({ queryKey: ['notifications'] })
}

export function useFileCliq(caseId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ suggestionId, caseId: target }: { suggestionId: string; caseId: string }) =>
      unwrap(
        fileCliqApiV1CliqSuggestionIdFilePost({
          path: { suggestion_id: suggestionId },
          body: { initiated_by: 'human', case_id: target },
        }),
      ),
    onSuccess: () => refresh(queryClient, caseId),
  })
}

export function useDismissCliq(caseId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (suggestionId: string) =>
      unwrap(
        dismissCliqApiV1CliqSuggestionIdDismissPost({
          path: { suggestion_id: suggestionId },
          body: { initiated_by: 'human' },
        }),
      ),
    onSuccess: () => refresh(queryClient, caseId),
  })
}

export function useReleaseCliq(caseId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (suggestionId: string) =>
      unwrap(
        releaseCliqApiV1CliqSuggestionIdReleasePost({
          path: { suggestion_id: suggestionId },
          body: { initiated_by: 'human' },
        }),
      ),
    onSuccess: () => refresh(queryClient, caseId),
  })
}
